'use client';

import { useState, useCallback } from 'react';
import type { AppError, AsyncResult } from '@shared/utils';
import type { ResultState } from '../types/ResultState';
import type { UseResultOptions } from '../types/UseResultOptions';
import type { UseResultReturn } from '../types/UseResultReturn';
import { useNotification } from './useNotification';

const INITIAL_STATE = <T, E>(): ResultState<T, E> => ({
  data: null,
  error: null,
  isLoading: false,
  isSuccess: false,
  isError: false,
});

/**
 * @group Hooks
 * @summary Core hook for managing async operations with the Result pattern.
 * Provides loading, error, and success states with automatic notification on errors.
 *
 * @example
 * ```tsx
 * function UserProfile({ userId }: { userId: string }) {
 *   const { execute, state } = useResult(
 *     () => tryCatchAsync(() => fetchUser(userId)),
 *     {
 *       notificationTitle: 'Failed to Load User',
 *       resource: 'user.profile'
 *     }
 *   );
 *
 *   useEffect(() => { execute(); }, [execute, userId]);
 *
 *   if (state.isLoading) return <Spinner />;
 *   if (state.isError) return <ErrorDisplay error={state.error} />;
 *   if (state.isSuccess) return <UserCard user={state.data} />;
 *   return null;
 * }
 * ```
 *
 * @example
 * ```tsx
 * // Silent operation (no notification or logging)
 * const { execute } = useResult(
 *   () => tryCatchAsync(prefetchData),
 *   { showNotification: false }
 * );
 * ```
 */
export function useResult<T, E = AppError>(
  asyncFn: () => AsyncResult<T, E>,
  options: UseResultOptions<T, E> = {}
): UseResultReturn<T, E> {
  const [state, setState] = useState<ResultState<T, E>>(INITIAL_STATE);
  const { notify } = useNotification();

  const execute = useCallback(async (): AsyncResult<T, E> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    const result = await asyncFn();

    result.match({
      ok: (data) => {
        setState({ data, error: null, isLoading: false, isSuccess: true, isError: false });
        options.onSuccess?.(data);
      },
      err: (error) => {
        setState({ data: null, error, isLoading: false, isSuccess: false, isError: true });
        options.onError?.(error);

        // Show notification unless explicitly suppressed
        if (options.showNotification !== false) {
          const appError = error as AppError;
          notify({
            title: options.notificationTitle || 'Error',
            message: appError.errorMessage || 'An unexpected error occurred',
            variant: options.notificationVariant || 'error',
            error: {
              error: appError,
              resource: options.resource || 'result.hook',
            },
          });
        }
      }
    });

    return result;
  }, [asyncFn, notify, options]);

  const reset = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  return { execute, state, reset };
}
